import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, MapPin, ExternalLink } from "lucide-react";

export default function AdminQueuePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (user === false) navigate("/login", { state: { from: "/admin/cafes" } });
    else if (user && user.role !== "admin") navigate("/", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cafes/pending");
      setPending(data);
    } catch (e) {
      toast("Couldn't load queue", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  const moderate = async (cafe, action, why = "") => {
    try {
      await api.post(`/admin/cafes/${cafe.id}/moderate`, { action, reason: why });
      toast(action === "approve" ? "Café approved — now live." : "Submission rejected.");
      setRejecting(null);
      setReason("");
      await load();
    } catch (e) {
      toast("Couldn't moderate", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  if (user && user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="admin-queue-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Editor desk</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Moderation queue</h1>
      <p className="text-[#786B63] mt-3 max-w-xl">
        {pending.length} submission{pending.length === 1 ? "" : "s"} waiting for review. Approve to publish, reject if it doesn't fit BrewFinder.
      </p>

      <div className="mt-10 space-y-5">
        {loading ? (
          [...Array(2)].map((_, i) => <div key={i} className="border border-border bg-card rounded-sm h-44 animate-pulse" />)
        ) : pending.length === 0 ? (
          <div className="border border-border bg-card rounded-sm p-12 text-center">
            <p className="font-serif text-2xl">Inbox zero. Nicely done.</p>
            <p className="text-sm text-[#786B63] mt-2">New submissions will land here as they come in.</p>
          </div>
        ) : pending.map((c) => (
          <div
            key={c.id}
            data-testid={`pending-cafe-${c.id}`}
            className="border border-border bg-card rounded-sm p-6 grid md:grid-cols-[160px_1fr_auto] gap-6"
          >
            <div className="aspect-square bg-[#E8DFD3] rounded-sm overflow-hidden">
              {c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#786B63]">
                Submitted by {c.submitted_by_name} · {new Date(c.created_at).toLocaleDateString()}
              </p>
              <h3 className="font-serif text-2xl mt-1">{c.name}</h3>
              <p className="text-xs text-[#786B63] mt-1 inline-flex items-center gap-1.5">
                <MapPin size={11} strokeWidth={1.5} /> {c.address}, {c.city}, {c.country}
              </p>
              <p className="text-sm text-[#2C1E16]/90 mt-3 line-clamp-3 leading-relaxed">{c.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(c.specialties || []).slice(0, 4).map((s) => (
                  <span key={s} className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-border rounded-full text-[#786B63]">{s}</span>
                ))}
              </div>
              {c.website && (
                <a href={c.website} target="_blank" rel="noreferrer" className="text-xs text-[#C05A46] inline-flex items-center gap-1 mt-3">
                  <ExternalLink size={12} strokeWidth={1.5} /> {c.website}
                </a>
              )}
            </div>
            <div className="flex md:flex-col gap-2">
              <Button
                size="sm"
                onClick={() => moderate(c, "approve")}
                className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]"
                data-testid={`approve-${c.id}`}
              >
                <Check className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRejecting(c)}
                className="rounded-sm"
                data-testid={`reject-${c.id}`}
              >
                <X className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Reject
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setReason(""); } }}>
        <DialogContent className="bg-[#F9F6F0] border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Reject "{rejecting?.name}"</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Optional internal note (why doesn't it fit?)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded-sm bg-[#F2EBE1]"
            rows={4}
            data-testid="reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setRejecting(null); setReason(""); }}>Cancel</Button>
            <Button
              className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0]"
              onClick={() => moderate(rejecting, "reject", reason)}
              data-testid="confirm-reject"
            >
              Reject submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
